/**
 * Serviço de autenticação.
 *
 * Envolve o `aws-amplify` para que o restante do painel não conheça a biblioteca, e para concentrar
 * três coisas que o legado errava:
 *
 *   1. Mensagens de erro em português e acionáveis, em vez do texto cru do Cognito.
 *   2. Fluxo real de primeira senha. O legado respondia ao desafio com a **mesma** senha temporária
 *      (achado 6), anulando a troca obrigatória — e com `this` em contexto errado, então nem
 *      funcionava.
 *   3. Validação de domínio no cadastro, que no legado era só rótulo no formulário (achado 1).
 */

import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
  type AuthSession,
} from 'aws-amplify/auth';

import { resolverPerfil, type PerfilAcesso } from '@emaster/dominio';

import { DOMINIO_CORPORATIVO } from './configuracao.ts';

// ---------------------------------------------------------------------------
// Contrato
// ---------------------------------------------------------------------------

/** Situação da tentativa de entrada. */
export type ResultadoEntrada =
  | { readonly tipo: 'autenticado' }
  | { readonly tipo: 'nova_senha_exigida' }
  | { readonly tipo: 'confirmacao_pendente'; readonly email: string };

export interface Sessao {
  readonly sub: string;
  readonly email: string;
  readonly perfil: PerfilAcesso | null;
  /** De onde veio o perfil: grupo do Cognito ou o atributo legado. */
  readonly origemPerfil: 'grupo' | 'custom_role' | 'nenhuma';
  /** `true` quando grupo e `custom:role` discordam. Detector de dessincronização. */
  readonly perfilDivergente: boolean;
  /** Token para o cabeçalho `Authorization` das chamadas à API. */
  readonly accessToken: string;
  /** Grupos do Cognito do usuário, para decisões que dependem de atribuição comercial. */
  readonly grupos: readonly string[];
}

export type CodigoErroAuth =
  | 'CREDENCIAL_INVALIDA'
  | 'USUARIO_DESABILITADO'
  | 'USUARIO_NAO_CONFIRMADO'
  | 'DOMINIO_NAO_PERMITIDO'
  | 'CODIGO_INVALIDO'
  | 'CODIGO_EXPIRADO'
  | 'SENHA_FRACA'
  | 'USUARIO_JA_EXISTE'
  | 'MUITAS_TENTATIVAS'
  | 'SESSAO_EXPIRADA'
  | 'DESCONHECIDO';

export class ErroAutenticacao extends Error {
  readonly codigo: CodigoErroAuth;

  constructor(codigo: CodigoErroAuth, mensagem: string) {
    super(mensagem);
    this.name = 'ErroAutenticacao';
    this.codigo = codigo;
  }
}

// ---------------------------------------------------------------------------
// Tradução de erros
// ---------------------------------------------------------------------------

const MENSAGENS: Readonly<Record<CodigoErroAuth, string>> = {
  CREDENCIAL_INVALIDA: 'E-mail ou senha incorretos.',
  USUARIO_DESABILITADO:
    'Esta conta está desabilitada. Procure um administrador para reativar o acesso.',
  USUARIO_NAO_CONFIRMADO:
    'Cadastro ainda não confirmado. Enviamos um novo código para o seu e-mail.',
  DOMINIO_NAO_PERMITIDO: `O cadastro é restrito a e-mails ${DOMINIO_CORPORATIVO}.`,
  CODIGO_INVALIDO: 'Código de verificação incorreto. Confira e tente novamente.',
  CODIGO_EXPIRADO: 'O código expirou. Solicite um novo.',
  SENHA_FRACA: 'A senha não atende à política de segurança exigida.',
  USUARIO_JA_EXISTE: 'Já existe uma conta com este e-mail.',
  MUITAS_TENTATIVAS: 'Muitas tentativas em sequência. Aguarde alguns minutos.',
  SESSAO_EXPIRADA: 'Sua sessão expirou. Entre novamente.',
  DESCONHECIDO: 'Não foi possível completar a operação. Tente novamente.',
};

/**
 * Traduz o erro do Cognito para um código próprio.
 *
 * O nome do erro é usado como chave, e não a mensagem, porque a mensagem varia entre versões da
 * biblioteca e não é contrato estável.
 */
export function traduzirErro(erro: unknown): ErroAutenticacao {
  const nome = erro instanceof Error ? erro.name : '';

  const mapa: Readonly<Record<string, CodigoErroAuth>> = {
    NotAuthorizedException: 'CREDENCIAL_INVALIDA',
    UserNotFoundException: 'CREDENCIAL_INVALIDA',
    UserDisabledException: 'USUARIO_DESABILITADO',
    UserNotConfirmedException: 'USUARIO_NAO_CONFIRMADO',
    CodeMismatchException: 'CODIGO_INVALIDO',
    ExpiredCodeException: 'CODIGO_EXPIRADO',
    InvalidPasswordException: 'SENHA_FRACA',
    UsernameExistsException: 'USUARIO_JA_EXISTE',
    LimitExceededException: 'MUITAS_TENTATIVAS',
    TooManyRequestsException: 'MUITAS_TENTATIVAS',
    TooManyFailedAttemptsException: 'MUITAS_TENTATIVAS',
  };

  const codigo = mapa[nome] ?? 'DESCONHECIDO';
  return new ErroAutenticacao(codigo, MENSAGENS[codigo]);
}

// ---------------------------------------------------------------------------
// Validação de e-mail
// ---------------------------------------------------------------------------

/** Verifica o domínio corporativo, sem diferenciar caixa. */
export function ehEmailCorporativo(email: string): boolean {
  return email.trim().toLowerCase().endsWith(DOMINIO_CORPORATIVO);
}

// ---------------------------------------------------------------------------
// Operações
// ---------------------------------------------------------------------------

/** Autentica por e-mail e senha. */
export async function entrar(email: string, senha: string): Promise<ResultadoEntrada> {
  const normalizado = email.trim().toLowerCase();

  try {
    const r = await signIn({ username: normalizado, password: senha });

    if (r.isSignedIn) return { tipo: 'autenticado' };

    switch (r.nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        return { tipo: 'nova_senha_exigida' };
      case 'CONFIRM_SIGN_UP':
        await resendSignUpCode({ username: normalizado }).catch(() => undefined);
        return { tipo: 'confirmacao_pendente', email: normalizado };
      default:
        throw new ErroAutenticacao(
          'DESCONHECIDO',
          `Este login exige uma etapa ainda não suportada pelo painel: ${r.nextStep.signInStep}.`,
        );
    }
  } catch (erro) {
    if (erro instanceof ErroAutenticacao) throw erro;
    throw traduzirErro(erro);
  }
}

/**
 * Completa o desafio de primeira senha.
 *
 * Corrige o achado 6: a senha temporária **não** é reaproveitada. O usuário informa uma senha nova,
 * e é ela que passa a valer.
 */
export async function definirNovaSenha(senhaNova: string): Promise<void> {
  try {
    const r = await confirmSignIn({ challengeResponse: senhaNova });
    if (!r.isSignedIn) {
      throw new ErroAutenticacao(
        'DESCONHECIDO',
        'A senha foi aceita, mas o login não foi concluído. Entre novamente.',
      );
    }
  } catch (erro) {
    if (erro instanceof ErroAutenticacao) throw erro;
    throw traduzirErro(erro);
  }
}

/** Cadastra um usuário novo, exigindo o domínio corporativo. */
export async function cadastrar(email: string, senha: string): Promise<void> {
  const normalizado = email.trim().toLowerCase();

  if (!ehEmailCorporativo(normalizado)) {
    throw new ErroAutenticacao('DOMINIO_NAO_PERMITIDO', MENSAGENS.DOMINIO_NAO_PERMITIDO);
  }

  try {
    await signUp({
      username: normalizado,
      password: senha,
      options: { userAttributes: { email: normalizado } },
    });
  } catch (erro) {
    throw traduzirErro(erro);
  }
}

/** Confirma o cadastro com o código enviado por e-mail. */
export async function confirmarCadastro(email: string, codigo: string): Promise<void> {
  try {
    await confirmSignUp({ username: email.trim().toLowerCase(), confirmationCode: codigo.trim() });
  } catch (erro) {
    throw traduzirErro(erro);
  }
}

/** Reenvia o código de confirmação. */
export async function reenviarCodigo(email: string): Promise<void> {
  try {
    await resendSignUpCode({ username: email.trim().toLowerCase() });
  } catch (erro) {
    throw traduzirErro(erro);
  }
}

/** Inicia a recuperação de senha. */
export async function iniciarRecuperacao(email: string): Promise<void> {
  try {
    await resetPassword({ username: email.trim().toLowerCase() });
  } catch (erro) {
    throw traduzirErro(erro);
  }
}

/** Conclui a recuperação de senha com o código recebido. */
export async function concluirRecuperacao(
  email: string,
  codigo: string,
  senhaNova: string,
): Promise<void> {
  try {
    await confirmResetPassword({
      username: email.trim().toLowerCase(),
      confirmationCode: codigo.trim(),
      newPassword: senhaNova,
    });
  } catch (erro) {
    throw traduzirErro(erro);
  }
}

/** Encerra a sessão. */
export async function sair(): Promise<void> {
  await signOut();
}

// ---------------------------------------------------------------------------
// Sessão
// ---------------------------------------------------------------------------

/** Extrai os grupos do `accessToken`, que é o token validado pelo autorizador da API. */
function extrairGrupos(sessao: AuthSession): string[] {
  const bruto = sessao.tokens?.accessToken.payload['cognito:groups'];
  if (!Array.isArray(bruto)) return [];
  return bruto.filter((g): g is string => typeof g === 'string');
}

/** Extrai um claim de texto do `idToken`. */
function claimTexto(sessao: AuthSession, chave: string): string | null {
  const valor = sessao.tokens?.idToken?.payload[chave];
  return typeof valor === 'string' ? valor : null;
}

/**
 * Devolve a sessão corrente, ou `null` quando não há usuário autenticado.
 *
 * O perfil é resolvido pela abstração do domínio, que dá precedência ao grupo do Cognito e aceita o
 * `custom:role` apenas como retaguarda — hoje necessária, porque o pool ainda não tem grupos.
 */
export async function obterSessao(): Promise<Sessao | null> {
  try {
    const usuario = await getCurrentUser();
    const sessao = await fetchAuthSession();

    const accessToken = sessao.tokens?.accessToken.toString();
    if (accessToken === undefined) return null;

    const resolvido = resolverPerfil(extrairGrupos(sessao), claimTexto(sessao, 'custom:role'));

    if (resolvido.divergente && import.meta.env.DEV) {
      console.warn(
        '[autenticacao] O grupo do Cognito e o custom:role apontam para perfis diferentes. ' +
          'O grupo tem precedência. Verifique a sincronização na gestão de usuários.',
      );
    }

    return {
      sub: usuario.userId,
      // O legado exibia o `sub` no lugar do e-mail (defeito D4). Aqui o e-mail vem do claim.
      email: claimTexto(sessao, 'email') ?? usuario.signInDetails?.loginId ?? usuario.username,
      perfil: resolvido.perfil,
      origemPerfil: resolvido.origem,
      perfilDivergente: resolvido.divergente,
      accessToken,
      grupos: extrairGrupos(sessao),
    };
  } catch {
    // `getCurrentUser` lança quando não há sessão. Ausência de sessão não é erro.
    return null;
  }
}

/**
 * Renova a sessão e devolve o `accessToken` válido.
 *
 * @throws {ErroAutenticacao} com código `SESSAO_EXPIRADA` quando não há como renovar.
 */
export async function obterTokenValido(): Promise<string> {
  const sessao = await fetchAuthSession({ forceRefresh: false });
  const token = sessao.tokens?.accessToken.toString();

  if (token === undefined) {
    throw new ErroAutenticacao('SESSAO_EXPIRADA', MENSAGENS.SESSAO_EXPIRADA);
  }
  return token;
}
